import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";

import Dropdown from "antd/es/dropdown";
import Menu from "antd/es/menu";
import isEmpty from "lodash-es/isEmpty";
import { useIntl } from "react-intl";

import { EntityType, EntityTypeSupportingGroups } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { LoadingSpinner } from "@mapmycustomers/ui";

import DashCard from "@app/component/DashCard";
import MoreButton from "@app/component/MoreButton";
import { DisplayType } from "@app/scene/home/enum/DisplayType";
import {
  getEngagementRecordsList,
  getRecordsOutOfCadence,
  getRecordsOutOfCadenceFilter,
  getTotalrecordsByEngagement,
  isRecordsOutOfCadenceLoading,
} from "@app/scene/home/store";
import {
  fetchEngagementRecordsList,
  fetchRecordsOutOfCadence,
  setRecordsOutOfCadenceFilter,
} from "@app/scene/home/store/actions";
import { getGroupsForEntity } from "@app/store/groups";
import { getTeams, getUsers } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { ClassName } from "@app/types/ui/ClassName";

import Chart from "./Chart";
import EmptyRecordsStats from "./EmptyRecordsStats";
import EntitySelector from "./EntitySelector";
import { GroupByType } from "./enum/GroupByType";
import FilterTypeSelect from "./FilterTypeSelect";
import { displayTypeMessages, generalMessages, groupByMessages } from "./messages";
import RecordTypesModal from "./RecordTypesModal";
import { GroupByEntity } from "./types/GroupByEntity";
import useGroupedEntities from "./util/useGroupedEntities";

interface Props {}

const EngagementCard: React.FC<
  Props & ClassName & typeof mapDispatchToProps & ReturnType<typeof mapStateToProps>
> = ({
  className,
  companyGroups,
  dealGroups,
  fetchEngagementRecordsList,
  fetchRecordsOutOfCadence,
  filter,
  isLoading,
  peopleGroups,
  recordsOutOfCadence,
  recordsOutOfCadenceList,
  setFilter,
  teams,
  totalRecords,
  users,
}) => {
  const intl = useIntl();

  const { displayType, groupByType, recordTypes } = filter;

  const [activityTypesDialogVisible, showActivityTypesDialog, hideActivityTypesDialog] =
    useBoolean();

  const list = useGroupedEntities(
    groupByType,
    recordTypes,
    users,
    teams,
    companyGroups,
    peopleGroups,
    dealGroups
  );

  const [usersDialogVisible, showUsersDialog, hideUsersDialog] = useBoolean();
  const [selectedEntities, setSelectedEntities] = useState<GroupByEntity[]>([]);

  const handleSelectRecordType = useCallback(
    () => showActivityTypesDialog(),
    [showActivityTypesDialog]
  );

  const handleShowGroupByEntitiesSelectDialog = useCallback(
    () => showUsersDialog(),
    [showUsersDialog]
  );

  useEffect(() => {
    const ids = selectedEntities.map(({ id }) => id);
    fetchRecordsOutOfCadence({
      [groupByType === GroupByType.TEAM
        ? "teamIds"
        : groupByType === GroupByType.GROUP
        ? "groupIds"
        : "userIds"]: ids,
    });
  }, [
    groupByType,
    fetchRecordsOutOfCadence,
    filter.groupByType,
    filter.recordTypes,
    selectedEntities,
  ]);

  const toggleDisplayType = useCallback(
    () =>
      setFilter({
        ...filter,
        displayType: displayType === DisplayType.PERCENT ? DisplayType.GROSS : DisplayType.PERCENT,
      }),
    [displayType, filter, setFilter]
  );

  const handleSelectedRecord = useCallback(
    (data, page) => {
      fetchEngagementRecordsList({ ...data, page, recordTypes });
    },
    [fetchEngagementRecordsList, recordTypes]
  );

  const setGroupByType = useCallback(
    (groupByType: GroupByType) => setFilter({ ...filter, groupByType }),
    [filter, setFilter]
  );

  const setRecordTypes = useCallback(
    (recordTypes: EntityTypeSupportingGroups[]) => setFilter({ ...filter, recordTypes }),
    [filter, setFilter]
  );

  return (
    <DashCard
      actions={[
        <FilterTypeSelect key="type" onChange={setGroupByType} value={groupByType} />,
        <Dropdown
          key="menu"
          overlay={
            <Menu>
              <Menu.Item onClick={handleShowGroupByEntitiesSelectDialog}>
                {intl.formatMessage(groupByMessages[groupByType])}
              </Menu.Item>
              <Menu.Item onClick={handleSelectRecordType}>
                {intl.formatMessage(generalMessages.menuItem2)}
              </Menu.Item>
              <Menu.Item onClick={toggleDisplayType}>
                {intl.formatMessage(displayTypeMessages[displayType])}
              </Menu.Item>
            </Menu>
          }
          placement="bottomRight"
          trigger={["click"]}
        >
          <MoreButton />
        </Dropdown>,
      ]}
      className={className}
      title={intl.formatMessage(generalMessages.title)}
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : isEmpty(recordsOutOfCadence) ? (
        <EmptyRecordsStats />
      ) : (
        <Chart
          displayType={displayType}
          groupByEntities={list}
          groupByType={groupByType}
          onSelectedData={handleSelectedRecord}
          records={recordsOutOfCadenceList}
          recordsOutOfCadence={recordsOutOfCadence}
          totalRecords={totalRecords}
        />
      )}
      <EntitySelector
        groupByType={groupByType}
        list={list}
        onHide={hideUsersDialog}
        onUpdateSelectedEntities={setSelectedEntities}
        recordTypes={recordTypes}
        selectedEntities={selectedEntities}
        visible={usersDialogVisible}
      />

      <RecordTypesModal
        onConfirm={setRecordTypes}
        onHide={hideActivityTypesDialog}
        selectedEntities={recordTypes}
        title={intl.formatMessage(generalMessages.recordTypesDialogTitle)}
        visible={activityTypesDialogVisible}
      />
    </DashCard>
  );
};

const mapStateToProps = (state: RootState) => ({
  companyGroups: getGroupsForEntity(state)(EntityType.COMPANY),
  dealGroups: getGroupsForEntity(state)(EntityType.DEAL),
  filter: getRecordsOutOfCadenceFilter(state),
  isLoading: isRecordsOutOfCadenceLoading(state),
  peopleGroups: getGroupsForEntity(state)(EntityType.PERSON),
  recordsOutOfCadence: getRecordsOutOfCadence(state),
  recordsOutOfCadenceList: getEngagementRecordsList(state),
  teams: getTeams(state),
  totalRecords: getTotalrecordsByEngagement(state),
  users: getUsers(state),
});

const mapDispatchToProps = {
  fetchEngagementRecordsList: fetchEngagementRecordsList.request,
  fetchRecordsOutOfCadence: fetchRecordsOutOfCadence.request,
  setFilter: setRecordsOutOfCadenceFilter,
};

export default connect(mapStateToProps, mapDispatchToProps)(EngagementCard);
